import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Star, ShoppingCart, Heart, Minus, Plus, AlertCircle, Tag, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { formatINR } from '../utils/currency';
import ProductGallery from '../components/pdp/ProductGallery';
import PriceBlock from '../components/pdp/PriceBlock';
import SocialProofBanner from '../components/pdp/SocialProofBanner';
import VariantSelector from '../components/pdp/VariantSelector';
import PincodeChecker from '../components/pdp/PincodeChecker';
import ProductDescriptionRenderer from '../components/ProductDescriptionRenderer';
import { Product } from '../types';
import ProductSkeleton from '../components/pdp/ProductSkeleton';
import { useCart } from '../context/CartContext';
import { wishlistService } from '../api/wishlist';
import { cartService } from '../api/cart';
import { useRequireCustomerAuth } from '../hooks/useRequireCustomerAuth';
import { productService } from '../api/products';
import { getAuthHeaders } from '../api';
import FrequentlyBought from '../components/pdp/FrequentlyBought';

// API base URL
const BACKEND_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper function to get full image URL
const getFullImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

/* ── Tab Content Helpers ─────────────────────────────── */

const DescriptionTabContent: React.FC<{ product: Product }> = ({ product }) => (
  <div className="space-y-8 text-slate-700">
    {product.productDescription?.isEnabled && product.productDescription?.sections && (
      <div className="rounded-xl border border-slate-100 bg-slate-50/30 p-1">
        <ProductDescriptionRenderer sections={product.productDescription.sections} position="description" />
      </div>
    )}

    <div>
      {product.content?.fullDescription && Array.isArray(product.content.fullDescription) && product.content.fullDescription.length > 0 ? (
        <div className="space-y-5">
          {product.content.fullDescription.map((block: any, index: number) => (
            <div key={index} className="description-block">
              {block.type === 'text' && (
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{block.content}</p>
              )}
              {block.type === 'heading' && (
                <h4 className="text-base sm:text-lg font-semibold text-slate-900 tracking-tight border-b border-slate-200 pb-2 mb-3">
                  {block.content}
                </h4>
              )}
              {block.type === 'image' && block.image && (
                <div className="my-3">
                  <img
                    src={getFullImageUrl(block.image)}
                    alt="Product description"
                    className={`rounded-xl mx-auto shadow-sm ring-1 ring-slate-900/5 ${
                      block.imageSize === 'small' ? 'max-w-xs' :
                      block.imageSize === 'large' ? 'max-w-2xl' :
                      block.imageSize === 'full' ? 'w-full' : 'max-w-xl'
                    }`}
                  />
                  {block.imageCaption && (
                    <p className="text-xs text-slate-500 text-center mt-2">{block.imageCaption}</p>
                  )}
                </div>
              )}
              {block.type === 'image-text' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  {block.image && (
                    <img src={getFullImageUrl(block.image)} alt="" className="rounded-xl w-full h-44 sm:h-48 object-cover shadow-sm ring-1 ring-slate-900/5" />
                  )}
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{block.content}</p>
                </div>
              )}
              {block.type === 'divider' && (
                <hr className="border-t border-slate-200 my-4" />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-3xl">
          {product.content?.description || product.description || 'No description available'}
        </p>
      )}
    </div>

    {product.content?.features && product.content.features.length > 0 && (
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Highlights</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {product.content.features.map((feature: string, index: number) => (
            <li
              key={index}
              className="flex items-start gap-2.5 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-sm text-slate-700"
            >
              <Star className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {product.offers && product.offers.length > 0 && (
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Offers</h3>
        <div className="space-y-2">
          {product.offers.map((offer: any, index: number) => (
            <div key={index} className="flex items-center gap-2.5 text-sm bg-emerald-50/80 border border-emerald-100/80 px-3 py-2.5 rounded-lg text-emerald-900">
              <Tag className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="font-medium">{offer.title || offer}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const InfoTabContent: React.FC<{ product: Product }> = ({ product }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/40 px-4 py-3 overflow-hidden">
      <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">SKU</dt>
      <dd className="mt-1 font-semibold text-slate-900 break-all">{product.sku || '—'}</dd>
    </div>
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/40 px-4 py-3 overflow-hidden">
      <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">Brand</dt>
      <dd className="mt-1 font-semibold text-slate-900 break-all">
        {(() => {
          if (typeof product.brand === 'object' && product.brand && product.brand.name) {
            return product.brand.name;
          }
          if (typeof product.brand === 'string' && /^[0-9a-fA-F]{24}$/.test(product.brand)) {
            return 'Premium Brand';
          }
          if (typeof product.brand === 'string' && product.brand.trim()) {
            return product.brand;
          }
          return 'Premium Brand';
        })()}
      </dd>
    </div>
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/40 px-4 py-3 overflow-hidden">
      <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">Category</dt>
      <dd className="mt-1 font-semibold text-slate-900 break-all">
        {typeof product.category === 'object' ? product.category?.name ?? '—' : product.category || '—'}
      </dd>
    </div>
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/40 px-4 py-3 overflow-hidden">
      <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">Weight</dt>
      <dd className="mt-1 font-semibold text-slate-900">
        {product.inventory?.weight != null
          ? `${product.inventory.weight} kg`
          : 'N/A'}
      </dd>
    </div>
  </dl>
);

const SpecsTabContent: React.FC<{ product: Product }> = ({ product }) => (
  product.specifications && product.specifications.length > 0 ? (
    <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
      {product.specifications.map((spec: any, index: number) => (
        <div
          key={index}
          className={`flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 px-4 py-3 text-sm ${
            index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
          }`}
        >
          <span className="text-slate-500 font-medium shrink-0">{spec.key}</span>
          <span className="text-slate-900 text-left sm:text-right font-semibold break-words">{spec.value}</span>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-slate-500 text-center py-10">No specifications available</p>
  )
);


const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { guardAction } = useRequireCustomerAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  // Sticky info tracking
  const infoRef = React.useRef<HTMLDivElement>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [effectivePrice, setEffectivePrice] = useState<number>(0);

  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<'description' | 'info' | 'specs'>('description');
  const [mobileAccordionTab, setMobileAccordionTab] = useState<string | null>(null);
  const [bundleProducts, setBundleProducts] = useState<Product[]>([]);
  const [isInfoSticky, setIsInfoSticky] = useState(false);

  // Calculate effective price based on selected variant (absolute pricing)
  useEffect(() => {
    if (!product) {
      setEffectivePrice(0);
      return;
    }

    const vp = product.variantPrices;
    if (!vp) {
      setEffectivePrice(product.price.selling);
      return;
    }

    let price = product.price.selling;

    // Priority: combination price > color price > size price > default
    if (selectedColor && selectedSize && vp.combinationPrices && typeof vp.combinationPrices[`${selectedColor}-${selectedSize}`] === 'number') {
      price = vp.combinationPrices[`${selectedColor}-${selectedSize}`];
    } else if (selectedColor && vp.colorPrices && typeof vp.colorPrices[selectedColor] === 'number') {
      price = vp.colorPrices[selectedColor];
    } else if (selectedSize && vp.sizePrices && typeof vp.sizePrices[selectedSize] === 'number') {
      price = vp.sizePrices[selectedSize];
    }

    setEffectivePrice(price);
  }, [product, selectedColor, selectedSize]);

  useEffect(() => {
    if (!slug) return;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await productService.getProductBySlug(slug);
        setProduct(response.data);
        // Initialize current images with product images
        const initialImages = response.data.images.map((img: string | { url: string }) => {
          const imageUrl = typeof img === 'string' ? img : img?.url;
          if (!imageUrl) return '';
          if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('uploads/')) {
            const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            return `${BACKEND_URL}${encodeURI(cleanPath)}`;
          }
          return imageUrl;
        }).filter((url: string) => url !== '');
        setCurrentImages(initialImages);
        setError(null);

        if (response.data) {
          fetchRelatedData(response.data);
          saveToRecentlyViewed(response.data._id);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  const fetchRelatedData = async (productData: Product) => {
    try {
      const categoryId = typeof productData.category === 'object'
        ? productData.category._id
        : productData.category;

      const response = await fetch(`${API_URL}/products?category=${categoryId}&limit=4&exclude=${productData._id}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setRelatedProducts(data.data.filter((p: Product) => p._id !== productData._id).slice(0, 4));
        // Use first 2 related products as bundle companions
        setBundleProducts(data.data.filter((p: Product) => p._id !== productData._id).slice(0, 2));
      }

      const recentlyViewedIds = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      if (recentlyViewedIds.length > 0) {
        const rvResponse = await fetch(`${API_URL}/products?ids=${recentlyViewedIds.join(',')}`, {
          headers: getAuthHeaders(),
        });
        const rvData = await rvResponse.json();
        if (rvData.success && Array.isArray(rvData.data)) {
          setRecentlyViewed(rvData.data.filter((p: Product) => p._id !== productData._id).slice(0, 4));
        }
      }
    } catch (error) {
      console.error('Failed to fetch related data:', error);
    }
  };
  
  const saveToRecentlyViewed = (productId: string) => {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const updated = [productId, ...recentlyViewed.filter((id: string) => id !== productId)].slice(0, 8);
    localStorage.setItem('recentlyViewed', JSON.stringify(updated));
  };

  const handleAddToCart = async () => {
    if (!product) return;
    setVariantError(null);
    const cartItem = {
      productId: product._id,
      name: product.name,
      mrp: product.price.mrp,
      price: effectivePrice > 0 ? effectivePrice : product.price.selling,
      quantity: quantity,
      image: currentImages.length > 0 ? currentImages[0] : (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url),
      variant: {
        color: selectedColor || undefined,
        size: selectedSize || undefined
      }
    };

    await guardAction(
      { type: 'addToCart', productId: product._id, quantity, localItem: cartItem },
      async () => {
        setIsAddingToCart(true);
        try {
          await cartService.addToCart(product._id, quantity);
          addToCart(cartItem);
          alert('Added to cart!');
        } finally {
          setIsAddingToCart(false);
        }
      }
    );
  };

  const handleBuyNow = async () => {
    if (!product) return;
    setVariantError(null);
    const buyNowProduct = {
      _id: product._id,
      productId: product._id,
      name: product.name,
      price: {
        selling: effectivePrice > 0 ? effectivePrice : product.price.selling,
        mrp: product.price.mrp
      },
      images: [{ url: currentImages.length > 0 ? currentImages[0] : (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url) }],
      quantity: quantity,
      variant: {
        color: selectedColor || undefined,
        size: selectedSize || undefined
      }
    };

    await guardAction(
      { type: 'buyNow', buyNowProduct },
      async () => {
        setIsAddingToCart(true);
        navigate('/checkout', { state: { isBuyNow: true, buyNowProduct } });
        setIsAddingToCart(false);
      }
    );
  };

  const handleWishlist = async () => {
    if (!product) return;
    await guardAction(
      { type: 'wishlist', productId: product._id, add: !isWishlisted },
      async () => {
        try {
          if (isWishlisted) {
            await wishlistService.removeFromWishlist(product._id);
            setIsWishlisted(false);
            alert('Removed from wishlist');
          } else {
            await wishlistService.addToWishlist(product._id);
            setIsWishlisted(true);
            alert('Added to wishlist');
          }
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Failed to update wishlist';
          console.error('Wishlist error:', error);
          alert(msg);
        }
      }
    );
  };

  // Sticky info scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!infoRef.current) return;
      const rect = infoRef.current.getBoundingClientRect();
      setIsInfoSticky(rect.top <= 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const incrementQuantity = () => {
    if (product && quantity < stockQty) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return <ProductSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The product you are looking for does not exist or has been removed.'}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/" 
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </Link>
            <Link 
              to="/products" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const productImages = product.images.map((img: string | { url: string }) => {
    const imageUrl = typeof img === 'string' ? img : img?.url;
    if (!imageUrl) return '';
    // Handle both /uploads/ and uploads/ paths
    if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('uploads/')) {
      const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${BACKEND_URL}${encodeURI(cleanPath)}`;
    }
    return imageUrl;
  }).filter((url: string) => url !== '');

  const stockQty = product.inventory?.stock ?? 0;
  const lowStockAlert = product.inventory?.lowStockAlert ?? 5;

  // Debug logs removed

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full max-w-[100vw] pb-28 md:pb-0 antialiased">
      {/* TOP: Sections before everything */}
      {product.productDescription?.isEnabled && product.productDescription?.sections && (
        <ProductDescriptionRenderer sections={product.productDescription.sections} position="top" />
      )}

      {/* Breadcrumb — compact, retail-style */}
      <div className="sticky top-0 z-20 w-full max-w-[100vw] border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3">
          <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-slate-500 overflow-x-auto whitespace-nowrap min-w-0" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-900 transition-colors shrink-0">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
            {(() => {
              const cat = product.category;
              const categorySlug = typeof cat === 'object' && cat?.slug ? cat.slug : typeof cat === 'string' ? cat : '';
              const categoryLabel = typeof cat === 'object' && cat?.name ? cat.name : typeof cat === 'string' ? cat : 'Category';
              return (
                <>
                  <Link to={categorySlug ? `/category/${categorySlug}` : '/products'} className="hover:text-slate-900 transition-colors shrink-0">
                    {categoryLabel}
                  </Link>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
                </>
              );
            })()}
            <span className="text-slate-900 font-medium truncate min-w-0" title={product.name}>{product.name}</span>
          </nav>
        </div>
      </div>

      {/* ABOVE PRODUCT: Sections before title/images */}
      {product.productDescription?.isEnabled && product.productDescription?.sections && (
        <ProductDescriptionRenderer sections={product.productDescription.sections} position="above-product" />
      )}

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6 md:py-8 overflow-x-hidden box-border">
        {/* Product grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start w-full min-w-0 max-w-full box-border">

          {/* Gallery — lg:col-span-6 */}
          <div className="lg:col-span-6 w-full min-w-0 order-1">
            <ProductGallery images={currentImages.length > 0 ? currentImages : productImages} productName={product.name} />
          </div>

          {/* Buy box — lg:col-span-6 */}
          <div
            ref={infoRef}
            className={`lg:col-span-6 w-full min-w-0 order-2 rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 lg:p-6 shadow-[0_1px_3px_rgba(15,23,42,0.06)] space-y-4 sm:space-y-5 transition-all duration-300 ${
              isInfoSticky ? 'lg:shadow-md' : ''
            }`}
          >
            {/* Title + brand */}
            <div className="border-b border-slate-100 pb-4 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 leading-snug tracking-tight break-words">
                {product.name}
              </h1>
              <p className="mt-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                {(() => {
                  if (typeof product.brand === 'object' && product.brand && product.brand.name) {
                    return product.brand.name;
                  }
                  if (typeof product.brand === 'string' && /^[0-9a-fA-F]{24}$/.test(product.brand)) {
                    return 'Premium Brand';
                  }
                  if (typeof product.brand === 'string' && product.brand.trim()) {
                    return product.brand;
                  }
                  return 'Premium Brand';
                })()}
              </p>
            </div>

            {/* Rating */}
            {(product.rating?.count || 0) > 0 && (
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200/80 bg-amber-50/90 px-3 py-1.5">
                <div className="flex items-center" aria-hidden>
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${
                        i < Math.floor(product.rating?.average || 0)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-900 tabular-nums">
                  {product.rating?.average?.toFixed(1) || '0.0'}
                </span>
                <span className="text-xs text-slate-500 tabular-nums">
                  ({product.rating?.count || 0} reviews)
                </span>
              </div>
            )}

            {/* Price */}
            <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
              <PriceBlock
                sellingPrice={effectivePrice > 0 ? effectivePrice : product.price.selling}
                mrp={product.price.mrp}
                offers={product.offers?.map((o: any) => o.title || o)}
              />
            </div>

            {/* Trust — only messaging we stand behind */}
            <div className="flex items-center justify-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2.5 text-xs sm:text-sm text-slate-600 font-medium">
              <ShieldCheck className="w-4 h-4 text-slate-700 shrink-0" aria-hidden />
              <span>Genuine product</span>
            </div>

            <SocialProofBanner
              stock={stockQty}
              lowStockThreshold={lowStockAlert}
            />

            <div className="rounded-xl border border-slate-100 bg-white p-3 sm:p-4">
              <PincodeChecker productId={product?._id} quantity={quantity} />
            </div>

            {/* Variants */}
            <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5">
              {/* Try multiple data sources for variants */}
              {(() => {
                const colors = (() => {
                  if (product.colors && product.colors.length > 0) {
                    return product.colors.map((c: any) => typeof c === 'object' ? c.name || c.value || c : c);
                  }
                  const variantColors = (product as any).variants?.find((v: any) => v.name === 'Color')?.options
                    || (product as any).variants?.find((v: any) => v.name === 'color')?.options
                    || (product as any).options?.find((v: any) => v.name === 'Color')?.options;
                  if (variantColors && variantColors.length > 0) {
                    return variantColors.map((c: any) => typeof c === 'object' ? c.name || c.value || c : c);
                  }
                  return [];
                })();

                const sizes = (() => {
                  if (product.sizes && product.sizes.length > 0) {
                    return product.sizes.map((s: any) => typeof s === 'object' ? s.name || s.value || s : s);
                  }
                  const variantSizes = (product as any).variants?.find((v: any) => v.name === 'Size')?.options
                    || (product as any).variants?.find((v: any) => v.name === 'size')?.options
                    || (product as any).options?.find((v: any) => v.name === 'Size')?.options;
                  if (variantSizes && variantSizes.length > 0) {
                    return variantSizes.map((s: any) => typeof s === 'object' ? s.name || s.value || s : s);
                  }
                  return [];
                })();

                if (colors.length === 0 && sizes.length === 0) {
                  return <p className="text-sm text-gray-500 text-center py-4">No variants available for this product</p>;
                }

                return (
                  <VariantSelector
                    colors={colors}
                    sizes={sizes}
                    selectedColor={selectedColor}
                    selectedSize={selectedSize}
                    onSelectColor={(color: string | null) => {
                      setSelectedColor(color);
                      setVariantError(null);

                      // Change images when color is selected
                      if (color && product.colorImages && product.colorImages[color]) {
                        const colorImageUrls = product.colorImages[color].map((img: string | { url: string }) => {
                          const imageUrl = typeof img === 'string' ? img : img?.url;
                          if (!imageUrl) return '';
                          if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('uploads/')) {
                            const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
                            return `${BACKEND_URL}${encodeURI(cleanPath)}`;
                          }
                          return imageUrl;
                        }).filter((url: string) => url !== '');
                        if (colorImageUrls.length > 0) {
                          setCurrentImages(colorImageUrls);
                        }
                      } else if (!color) {
                        // Reset to default images when color is deselected
                        const defaultImages = product.images.map((img: string | { url: string }) => {
                          const imageUrl = typeof img === 'string' ? img : img?.url;
                          if (!imageUrl) return '';
                          if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('uploads/')) {
                            const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
                            return `${BACKEND_URL}${encodeURI(cleanPath)}`;
                          }
                          return imageUrl;
                        }).filter((url: string) => url !== '');
                        setCurrentImages(defaultImages);
                      }
                    }}
                    onSelectSize={(size: string | null) => { setSelectedSize(size); setVariantError(null); }}
                    variantError={variantError}
                  />
                );
              })()}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2">
                Quantity
              </label>
              <div className="inline-flex items-stretch rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={decrementQuantity}
                  className="px-3 py-2.5 hover:bg-slate-50 disabled:opacity-40 transition-colors bg-slate-50/80"
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4 text-slate-700" />
                </button>
                <span className="min-w-[2.75rem] px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-slate-900 border-x border-slate-200 bg-white">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={incrementQuantity}
                  className="px-3 py-2.5 hover:bg-slate-50 disabled:opacity-40 transition-colors bg-slate-50/80"
                  disabled={quantity >= stockQty}
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4 text-slate-700" />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isAddingToCart || stockQty <= 0}
                className="flex-1 min-h-[48px] rounded-xl bg-slate-900 text-white py-3 px-4 text-sm font-semibold hover:bg-slate-800 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 shadow-sm transition-all active:scale-[0.99]"
              >
                <ShoppingCart className="w-4 h-4 shrink-0" aria-hidden />
                <span className="truncate">{isAddingToCart ? 'Adding…' : 'Add to cart'}</span>
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={stockQty <= 0}
                className="flex-1 min-h-[48px] rounded-xl bg-orange-500 text-white py-3 px-4 text-sm font-semibold hover:bg-orange-600 disabled:opacity-45 disabled:cursor-not-allowed shadow-sm transition-all active:scale-[0.99]"
              >
                Buy now
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleWishlist}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-xs font-semibold transition-colors ${
                  isWishlisted
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-rose-200 hover:text-rose-600'
                }`}
              >
                <Heart className={`w-4 h-4 shrink-0 ${isWishlisted ? 'fill-current' : ''}`} aria-hidden />
                <span>{isWishlisted ? 'Saved' : 'Wishlist'}</span>
              </button>
            </div>

          </div>
        </div>

        {/* BELOW PRODUCT: Sections after title/price */}
        {product.productDescription?.isEnabled && product.productDescription?.sections && (
          <ProductDescriptionRenderer sections={product.productDescription.sections} position="below-product" />
        )}

        {/* Tabs */}
        <div id="product-tabs" className="w-full mt-10 lg:mt-12">
          {/* Desktop tabs */}
          <div className="hidden sm:block rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-200 bg-slate-50/60 px-1 pt-1 gap-0.5 overflow-x-auto">
              {(['description', 'info', 'specs'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`relative px-5 py-3 text-sm font-semibold transition-colors rounded-t-lg whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-slate-900 bg-white shadow-sm border border-slate-200 border-b-white -mb-px'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {tab === 'description' && 'Description'}
                  {tab === 'info' && 'Product information'}
                  {tab === 'specs' && 'Specifications'}
                </button>
              ))}
            </div>
            <div className="p-5 sm:p-8 overflow-hidden">
              {activeTab === 'description' && <DescriptionTabContent product={product} />}
              {activeTab === 'info' && <InfoTabContent product={product} />}
              {activeTab === 'specs' && <SpecsTabContent product={product} />}
            </div>
          </div>

          {/* Mobile accordion */}
          <div className="sm:hidden rounded-2xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100 overflow-hidden">
            {(['description', 'info', 'specs'] as const).map((tab) => (
              <div key={tab}>
                <button
                  type="button"
                  onClick={() => setMobileAccordionTab(mobileAccordionTab === tab ? null : tab)}
                  className="w-full flex items-center justify-between px-4 py-4 text-left text-base font-semibold text-slate-900 active:bg-slate-50/80"
                >
                  <span>
                    {tab === 'description' && 'Description'}
                    {tab === 'info' && 'Product information'}
                    {tab === 'specs' && 'Specifications'}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 transition-transform shrink-0 ${
                      mobileAccordionTab === tab ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {mobileAccordionTab === tab && (
                  <div className="px-4 pb-5 pt-0 border-t border-slate-100">
                    <div className="pt-4">
                      {tab === 'description' && <DescriptionTabContent product={product} />}
                      {tab === 'info' && <InfoTabContent product={product} />}
                      {tab === 'specs' && <SpecsTabContent product={product} />}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Frequently Bought Together */}
        {bundleProducts.length > 0 && (
          <FrequentlyBought mainProduct={product} companions={bundleProducts} />
        )}

        {/* ABOVE RELATED: Sections before related products */}
        {product.productDescription?.isEnabled && product.productDescription?.sections && (
          <ProductDescriptionRenderer sections={product.productDescription.sections} position="above-related" />
        )}

        {/* Related Products - Flipkart Style Horizontal Carousel */}
        {relatedProducts.length > 0 && (
          <div className="mt-10 sm:mt-12">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight">You may also like</h2>
                <p className="text-sm text-slate-500 mt-1">Similar items in this category</p>
              </div>
            </div>
            <div
              className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
              style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
            >
              {relatedProducts.map((relatedProduct) => {
                const productLink = relatedProduct.slug || relatedProduct._id;
                return (
                  <Link
                    key={relatedProduct._id}
                    to={`/product/${productLink}`}
                    className="snap-start flex-shrink-0 w-[9.5rem] sm:w-44 md:w-52 group bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img
                      src={(() => {
                        const img = relatedProduct.images[0];
                        const imgUrl = typeof img === 'string' ? img : img?.url;
                        if (!imgUrl) return '/placeholder-product.jpg';
                        if (imgUrl.startsWith('/uploads') || imgUrl.startsWith('uploads/')) {
                          const cleanPath = imgUrl.startsWith('/') ? imgUrl : `/${imgUrl}`;
                          return `${BACKEND_URL}${encodeURI(cleanPath)}`;
                        }
                        return imgUrl;
                      })()}
                      alt={relatedProduct.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-product.jpg';
                      }}
                    />
                  </div>
                  <div className="p-3 min-h-[4.5rem]">
                    <h3 className="text-xs sm:text-sm font-semibold text-slate-900 line-clamp-2 leading-snug mb-2">{relatedProduct.name}</h3>
                    <div className="flex items-center gap-2 sm:gap-2">
                      <span className="text-sm sm:text-sm font-bold text-gray-900">{formatINR(relatedProduct.price.selling)}</span>
                      {relatedProduct.price.selling < relatedProduct.price.mrp && (
                        <span className="text-xs sm:text-xs text-gray-400 line-through">{formatINR(relatedProduct.price.mrp)}</span>
                      )}
                    </div>
                    {relatedProduct.price.selling < relatedProduct.price.mrp && (
                      <div className="mt-2">
                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-md font-bold">
                          {Math.round(((relatedProduct.price.mrp - relatedProduct.price.selling) / relatedProduct.price.mrp) * 100)}% off
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
            </div>
          </div>
        )}

        {/* AFTER RELATED: Sections after related products */}
        {product.productDescription?.isEnabled && product.productDescription?.sections && (
          <ProductDescriptionRenderer sections={product.productDescription.sections} position="after-related" />
        )}

        {/* Recently Viewed - CLICKABLE */}
        {recentlyViewed.length > 0 && (
          <div className="mt-12 sm:mt-16">
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight mb-4">Recently viewed</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {recentlyViewed.map((viewedProduct) => {
                const viewedLink = viewedProduct.slug || viewedProduct._id;
                return (
                  <Link
                    key={viewedProduct._id}
                    to={`/product/${viewedLink}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img
                      src={(() => {
                        const img = viewedProduct.images[0];
                        const imgUrl = typeof img === 'string' ? img : img?.url;
                        if (!imgUrl) return '/placeholder-product.jpg';
                        if (imgUrl.startsWith('/uploads') || imgUrl.startsWith('uploads/')) {
                          const cleanPath = imgUrl.startsWith('/') ? imgUrl : `/${imgUrl}`;
                          return `${BACKEND_URL}${encodeURI(cleanPath)}`;
                        }
                        return imgUrl;
                      })()}
                      alt={viewedProduct.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-product.jpg';
                      }}
                    />
                  </div>
                  <div className="p-2">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{viewedProduct.name}</h3>
                    <span className="text-xs sm:text-sm font-bold text-blue-600">{formatINR(viewedProduct.price.selling)}</span>
                  </div>
                </Link>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM: Sections at footer area */}
      {product.productDescription?.isEnabled && product.productDescription?.sections && (
        <ProductDescriptionRenderer sections={product.productDescription.sections} position="bottom" />
      )}

      {/* Sticky Bottom CTA Bar - Mobile Only */}
      {product && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur-md px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(15,23,42,0.08)]">
          {stockQty > 0 && (selectedColor || selectedSize) && (
            <div className="text-[10px] text-slate-500 text-center mb-1.5 truncate">
              {selectedColor && selectedSize
                ? `${selectedColor} · ${selectedSize}`
                : selectedColor || selectedSize}
            </div>
          )}
          {stockQty <= 0 && (
            <div className="text-xs font-medium text-rose-600 text-center mb-1.5">
              Out of stock
            </div>
          )}
          {stockQty > 0 && stockQty <= lowStockAlert && (
            <div className="text-[10px] text-amber-700 text-center mb-1.5 font-medium">
              Only {stockQty} left
            </div>
          )}
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <div className="shrink-0">
              <span className="text-lg font-semibold text-slate-900 tabular-nums">
                {formatINR(effectivePrice > 0 ? effectivePrice : product.price.selling)}
              </span>
              {product.price.mrp > (effectivePrice > 0 ? effectivePrice : product.price.selling) && (
                <span className="text-xs text-slate-400 line-through ml-1.5 tabular-nums">{formatINR(product.price.mrp)}</span>
              )}
            </div>
            <div className="flex-1 flex gap-2 min-w-0">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isAddingToCart || stockQty <= 0}
                className="flex-1 min-h-[44px] rounded-xl border border-slate-200 bg-white text-slate-900 text-sm font-semibold hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-1.5 transition-all active:scale-[0.99]"
              >
                <ShoppingCart className="w-4 h-4 shrink-0" aria-hidden />
                <span>{isAddingToCart ? '…' : 'Cart'}</span>
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={isAddingToCart || stockQty <= 0}
                className="flex-1 min-h-[44px] rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-40 shadow-sm transition-all active:scale-[0.99]"
              >
                Buy now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
